import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  LocalTracksState, SetTrackPayload,
} from "./types";
import { uuidv4 } from "utils";

/** Store serializable data for local tracks that are used by many components.
 * Decided not to store the created media tracks in state here for efficiency.
 * This data should be used by the lobby, room, and local participant components
 * to create/control media tracks when and where they are necessary.
 */

export const localTracks = createSlice({
  name: "localTracks",
  initialState: {
    busy: {
      audio: false,
      video: false
    },
    localVideoTrack: undefined,
    localAudioTrack: undefined,
    localDataTrack: undefined,
    videoTrackInfo: undefined,
    audioTrackInfo: undefined,
    audioOutput: undefined,
    hideVideo: false,
    muteAudio: false,
    shareScreen: false,
    showLocalTrackControls: false,
    showOnlyLocalVideo: false,
  } as LocalTracksState,
  reducers: {
    setBusy: (state, { payload }: PayloadAction<{ audio?: boolean; video?: boolean; }>) => {
      state.busy = {
        ...state.busy,
        ...payload,
      };
    },
    setLocalVideoTrack: (state, { payload }: PayloadAction<any>) => {
      state.localVideoTrack = payload;
    },
    setLocalAudioTrack: (state, { payload }: PayloadAction<any>) => {
      state.localAudioTrack = payload;
    },
    setLocalDataTrack: (state, { payload }: PayloadAction<any>) => {
      state.localDataTrack = payload;
    },
    setLocalVideoTrackInfo: (state, { payload }: PayloadAction<SetTrackPayload>) => {
      state.videoTrackInfo = { name: `${uuidv4()}-camera`, deviceId: payload.deviceId, label: payload.label };
    },
    setLocalAudioTrackInfo: (state, { payload }: PayloadAction<SetTrackPayload>) => {
      state.audioTrackInfo = { deviceId: payload.deviceId, label: payload.label, isDefault: payload.isDefault };
    },
    setAudioOutput: (state, { payload }: PayloadAction<SetTrackPayload>) => {
      state.audioOutput = { deviceId: payload.deviceId, label: payload.label, isDefault: payload.isDefault };
    },
    toggleVideo: (state, { payload }: PayloadAction<boolean>) => {
      state.hideVideo = payload;
    },
    toggleAudio: (state, { payload }: PayloadAction<boolean>) => {
      state.muteAudio = payload;
    },
    toggleScreenShare: (state, { payload }: PayloadAction<boolean>) => {
      state.shareScreen = payload;
    },
    toggleLocalTrackControls: (state, { payload }: PayloadAction<boolean>) => {
      state.showLocalTrackControls = payload;
    },
    toggleShowOnlyLocalVideo: (state) => {
      state.showOnlyLocalVideo = !state.showOnlyLocalVideo;
    },
  },
});

export const {
  setBusy,
  setLocalAudioTrack,
  setLocalVideoTrack,
  setLocalDataTrack,
  setLocalAudioTrackInfo,
  setLocalVideoTrackInfo,
  setAudioOutput,
  toggleAudio,
  toggleVideo,
  toggleScreenShare,
  toggleLocalTrackControls,
  toggleShowOnlyLocalVideo
} = localTracks.actions;

export default localTracks.reducer;
